import { useState } from "react";
import { motion } from "framer-motion";
import { MapPin, Plane } from "lucide-react";
import { Reveal } from "@/lib/motion";
import { HUBS } from "@/lib/site-data";

const STATS = [
  { value: "3", label: "Origin countries" },
  { value: "Jamaica", label: "Delivered nationwide" },
  { value: "4×", label: "Weekly departures" },
  { value: "99.7%", label: "On-time delivery" },
];

export default function Coverage() {
  const [active, setActive] = useState(null);
  const dest = HUBS.find((h) => h.role === "dest");
  const origins = HUBS.filter((h) => h.role === "origin");

  return (
    <section id="coverage" className="relative py-24 sm:py-32">
      <div className="mx-auto max-w-[1320px] px-5 sm:px-8">
        <div className="grid gap-12 lg:grid-cols-[0.9fr_1.1fr] lg:items-center">
          <Reveal>
            <div className="mb-4 flex items-center gap-3 text-xs font-semibold uppercase tracking-[0.22em] text-[#E31B23]">
              <span className="h-px w-8 bg-[#E31B23]" /> Global network
            </div>
            <h2 className="font-display text-4xl font-extrabold leading-tight text-[#1F1F1F] sm:text-5xl">
              From the USA, Canada &amp; China — straight to Jamaica.
            </h2>
            <p className="mt-4 max-w-md text-[#4b5563]">
              We consolidate your cargo across three major gateways and ship it home to Jamaica by
              air and sea, every week. Hover a gateway to see transit times.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-5">
              {STATS.map((s) => (
                <div key={s.label} className="card-soft rounded-2xl p-5">
                  <div className="font-display text-3xl font-extrabold text-[#1F1F1F]">{s.value}</div>
                  <div className="mt-1 text-xs uppercase tracking-wider text-[#9ca3af]">{s.label}</div>
                </div>
              ))}
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="relative aspect-[4/3] w-full overflow-hidden rounded-3xl border border-black/5 bg-[#F5F5F7]">
              <div className="absolute inset-0" style={{
                backgroundImage: "radial-gradient(rgba(90,24,154,0.28) 1px, transparent 1.4px)",
                backgroundSize: "20px 20px",
                WebkitMaskImage: "radial-gradient(ellipse 72% 62% at 50% 50%, black 55%, transparent 100%)",
                maskImage: "radial-gradient(ellipse 72% 62% at 50% 50%, black 55%, transparent 100%)",
              }} />

              {/* arcs from each origin to Jamaica */}
              <svg className="absolute inset-0 h-full w-full" preserveAspectRatio="none" viewBox="0 0 100 100">
                {origins.map((o, i) => {
                  const mx = (o.left + dest.left) / 2;
                  const my = Math.min(o.top, dest.top) - 14;
                  return (
                    <motion.path key={o.name} d={`M ${o.left} ${o.top} Q ${mx} ${my} ${dest.left} ${dest.top}`}
                      fill="none" stroke="url(#arcg)" strokeWidth="0.5"
                      initial={{ pathLength: 0, opacity: 0 }} whileInView={{ pathLength: 1, opacity: 1 }} viewport={{ once: true }}
                      transition={{ duration: 1.4, delay: 0.4 + i * 0.25, ease: "easeInOut" }} />
                  );
                })}
                <defs>
                  <linearGradient id="arcg" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#5A189A" /><stop offset="100%" stopColor="#E31B23" />
                  </linearGradient>
                </defs>
              </svg>

              {/* origin gateways */}
              {origins.map((h, i) => (
                <div key={h.name} className="absolute" style={{ left: `${h.left}%`, top: `${h.top}%` }}
                  onMouseEnter={() => setActive(h.name)} onMouseLeave={() => setActive(null)}
                  data-testid={`hub-${h.name.toLowerCase()}`}>
                  <span className="absolute -translate-x-1/2 -translate-y-1/2 h-3 w-3 rounded-full bg-[#5A189A] node-pulse" style={{ animationDelay: `${i * 0.3}s` }} />
                  <span className="absolute -translate-x-1/2 -translate-y-1/2 h-2.5 w-2.5 rounded-full bg-[#5A189A] cursor-pointer" />
                  {active === h.name ? (
                    <div className="absolute left-3 -top-11 z-10 whitespace-nowrap rounded-xl bg-white px-3 py-2 text-left shadow-[0_10px_24px_-12px_rgba(31,31,31,0.5)] border border-black/5">
                      <div className="text-xs font-bold text-[#1F1F1F]">{h.name} · {h.city}</div>
                      <div className="text-[11px] text-[#4b5563]">Transit to Jamaica: {h.time}</div>
                    </div>
                  ) : (
                    <span className="absolute left-3 -top-2 whitespace-nowrap text-[11px] font-semibold text-[#1F1F1F]">{h.name}</span>
                  )}
                </div>
              ))}

              {/* destination: Jamaica */}
              <div className="absolute" style={{ left: `${dest.left}%`, top: `${dest.top}%` }} data-testid="hub-jamaica">
                <span className="absolute -translate-x-1/2 -translate-y-1/2 h-5 w-5 rounded-full bg-[#E31B23]/25 node-pulse" />
                <span className="absolute flex h-7 w-7 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-[#E31B23] shadow-[0_6px_16px_-4px_rgba(227,27,35,0.7)]">
                  <MapPin size={14} className="text-white" />
                </span>
                <span className="absolute left-5 -top-2 whitespace-nowrap font-display text-xs font-extrabold text-[#E31B23]">Jamaica</span>
              </div>

              <div className="absolute bottom-3 right-4 flex items-center gap-1.5 text-[10px] font-medium text-[#9ca3af]">
                <Plane size={12} className="text-[#5A189A]" /> Air &amp; sea to Jamaica, weekly
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
